import { useState } from "react";
import {
  Globe,
  Clock,
  Eye,
  Home,
  Lightbulb,
  ImageOff,
  Lock,
  MapPin,
  ExternalLink,
} from "lucide-react";
import { AdvancedMarker, Pin } from "@vis.gl/react-google-maps";
import { IconMapPinFilled } from "@tabler/icons-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { GoogleMapWrapper } from "@/components/map/GoogleMapWrapper";
import { sportTypeIcons } from "@/lib/sport-icons";
import { getSportLabel } from "@/lib/sport-defaults";
import { VerdictControl } from "./VerdictControl";
import { CommentList } from "./CommentList";
import { CommentForm } from "./CommentForm";
import { PhotoLightbox } from "./PhotoLightbox";
import type { CityReportStation } from "./types";

interface StationDetailSidebarProps {
  station: CityReportStation | null;
  token: string;
  clientId: string;
  readOnly: boolean;
  onClose: () => void;
}

function formatSport(s: string | null): string {
  if (!s) return "Unassigned";
  return getSportLabel(s);
}

function BoolRow({
  Icon,
  label,
  value,
  detail,
}: {
  Icon: typeof Globe;
  label: string;
  value: boolean | null;
  detail?: string | null;
}) {
  const text =
    value === true ? "Yes" : value === false ? "No" : "Not recorded";
  const tone =
    value === true
      ? "text-green-700 dark:text-green-400"
      : value === false
        ? "text-muted-foreground"
        : "text-muted-foreground/70";
  return (
    <div className="flex items-start gap-3 py-1.5">
      <Icon className="size-4 text-muted-foreground mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm">{label}</div>
        {detail && (
          <div className="text-xs text-muted-foreground truncate">
            {detail}
          </div>
        )}
      </div>
      <span className={cn("text-sm font-medium", tone)}>{text}</span>
    </div>
  );
}

export function StationDetailSidebar({
  station,
  token,
  clientId,
  readOnly,
  onClose,
}: StationDetailSidebarProps) {
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const open = !!station;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[480px] overflow-y-auto p-0"
      >
        {station && (
          <>
            <SheetHeader className="p-5 pb-3 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge
                  variant={station.role === "main" ? "default" : "secondary"}
                >
                  {station.role === "main" ? "Main" : "Backup"}
                </Badge>
                {station.admin_tier != null && (
                  <Badge variant="outline">Tier {station.admin_tier}</Badge>
                )}
              </div>
              <SheetTitle className="text-xl">{station.label}</SheetTitle>
              {station.address && (
                <div className="flex items-start gap-1.5 text-sm text-muted-foreground">
                  <MapPin className="size-3.5 mt-0.5 shrink-0" />
                  <span>{station.address}</span>
                </div>
              )}
            </SheetHeader>

            <Separator />

            <div className="p-5 space-y-5">
              <div>
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  Location
                </h3>
                <div className="h-[200px] w-full rounded-md overflow-hidden border">
                  <GoogleMapWrapper
                    defaultCenter={{ lat: station.lat, lng: station.lng }}
                    defaultZoom={17}
                    mapTypeId="hybrid"
                    disableDefaultUI
                    className="h-full w-full"
                  >
                    <AdvancedMarker
                      position={{ lat: station.lat, lng: station.lng }}
                    >
                      <Pin
                        background="#ef4444"
                        borderColor="#991b1b"
                        glyphColor="#ffffff"
                      />
                    </AdvancedMarker>
                  </GoogleMapWrapper>
                </div>
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="w-full mt-2"
                >
                  <a
                    href={`https://www.google.com/maps?q=${station.lat},${station.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="size-3.5 mr-1.5" />
                    Open in Google Maps
                  </a>
                </Button>
              </div>

              {station.photos.length > 0 ? (
                <div>
                  <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                    Photos
                  </h3>
                  <div className="flex gap-2 overflow-x-auto scrollbar-none">
                    {station.photos.map((p, i) => (
                      <button
                        key={p.id}
                        type="button"
                        className="size-20 shrink-0 rounded-lg overflow-hidden bg-muted relative"
                        onClick={() => setLightboxIdx(i)}
                      >
                        <img
                          src={p.url}
                          alt=""
                          className="size-full object-cover hover:opacity-90 transition-opacity"
                          loading="lazy"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-4 text-sm text-muted-foreground">
                  <ImageOff className="size-4" />
                  No photos captured for this station.
                </div>
              )}

              <div>
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  Lockers ({station.lockers.length})
                </h3>
                {station.lockers.length > 0 ? (
                  <ul className="space-y-1">
                    {station.lockers.map((l) => {
                      const SportIcon = l.sport_type
                        ? (sportTypeIcons[l.sport_type] ?? IconMapPinFilled)
                        : null;
                      return (
                        <li
                          key={l.locker_index}
                          className="flex items-center justify-between text-sm border rounded-md px-3 py-1.5 bg-muted/20"
                        >
                          <span className="text-muted-foreground">
                            Locker {l.locker_index}
                          </span>
                          <span
                            className={cn(
                              "inline-flex items-center gap-1.5 font-medium",
                              !l.sport_type && "text-muted-foreground italic",
                            )}
                          >
                            {SportIcon && (
                              <SportIcon
                                size={16}
                                stroke={1.75}
                                className="text-foreground/70 shrink-0"
                              />
                            )}
                            {formatSport(l.sport_type)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No lockers assigned yet.
                  </p>
                )}
              </div>

              <div>
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  Conditions
                </h3>
                <div className="rounded-md border divide-y">
                  <div className="px-3">
                    <BoolRow
                      Icon={Globe}
                      label="Public access"
                      value={station.conditions.public_access}
                    />
                  </div>
                  <div className="px-3">
                    <BoolRow
                      Icon={Clock}
                      label="Open 24/7"
                      value={station.conditions.open_24_7}
                      detail={
                        station.conditions.open_24_7 === false
                          ? station.conditions.opening_hours
                          : null
                      }
                    />
                  </div>
                  <div className="px-3">
                    <BoolRow
                      Icon={Eye}
                      label="Passing-by visibility"
                      value={station.conditions.passing_by_visibility}
                    />
                  </div>
                  <div className="px-3">
                    <BoolRow
                      Icon={Home}
                      label="Nearby habitations"
                      value={station.conditions.nearby_habitations}
                    />
                  </div>
                  <div className="px-3">
                    <BoolRow
                      Icon={Lightbulb}
                      label="Lighting present"
                      value={station.conditions.lighting_present}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {readOnly && (
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
                  <Lock className="size-3.5 mt-0.5 shrink-0" />
                  This report is closed. Verdicts and comments can no longer be
                  changed.
                </div>
              )}

              <VerdictControl
                token={token}
                pilId={station.id}
                current={station.city_verdict}
                disabled={readOnly}
              />

              <Separator />

              <div className="space-y-3">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Comments
                </h3>
                <CommentList
                  token={token}
                  pilId={station.id}
                  clientId={clientId}
                  readOnly={readOnly}
                />
                {!readOnly && clientId && (
                  <CommentForm
                    token={token}
                    pilId={station.id}
                    clientId={clientId}
                  />
                )}
              </div>
            </div>

            <PhotoLightbox
              photos={station.photos}
              index={lightboxIdx}
              onClose={() => setLightboxIdx(null)}
              onIndexChange={setLightboxIdx}
            />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
